import { create } from 'zustand'
import { supabase, getSession } from '../lib/supabase'
import { userApi, clearCache, CACHE_KEYS } from '../lib/api'
import type { User } from '../types'

interface AuthState {
  user: User | null
  session: any | null
  loading: boolean
  error: string | null
  lastRefreshed: number | null

  // Actions
  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshUser: (force?: boolean) => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  error: null,
  lastRefreshed: null,

  initialize: async () => {
    try {
      set({ loading: true, error: null })
      const session = await getSession()

      if (session) {
        set({ session })
        const user = await userApi.getMe()
        set({ user, lastRefreshed: Date.now() })
      }
    } catch (error: any) {
      console.error('Auth init error:', error)
      set({ error: error.message })
    } finally {
      set({ loading: false })
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (_event, session) => {
      set({ session })
      if (session) {
        try {
          const user = await userApi.getMe(true) // Force refresh on auth change
          set({ user, lastRefreshed: Date.now() })
        } catch {
          set({ user: null, lastRefreshed: null })
        }
      } else {
        set({ user: null, lastRefreshed: null })
        clearCache() // Clear all cache on sign out
      }
    })
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error

      set({ session: data.session })
      const user = await userApi.getMe(true) // Force fresh fetch on sign in
      set({ user, lastRefreshed: Date.now() })
    } catch (error: any) {
      set({ error: error.message })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  signUp: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })
      if (error) throw error

      // Note: User may need to verify email before session is active
      if (data.session) {
        set({ session: data.session })
        const user = await userApi.getMe(true) // Force fresh fetch on sign up
        set({ user, lastRefreshed: Date.now() })
      }
    } catch (error: any) {
      set({ error: error.message })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  signOut: async () => {
    try {
      set({ loading: true, error: null })
      await supabase.auth.signOut()
      set({ user: null, session: null })
    } catch (error: any) {
      set({ error: error.message })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  refreshUser: async (force = false) => {
    const state = get()
    const now = Date.now()

    // Skip if refreshed within last 10 seconds (unless forced)
    if (!force && state.lastRefreshed && (now - state.lastRefreshed) < 10000) {
      return
    }

    try {
      // Clear cache and fetch fresh
      clearCache(CACHE_KEYS.USER)
      const user = await userApi.getMe(true)
      set({ user, lastRefreshed: now })
    } catch (error: any) {
      console.error('Refresh user error:', error)
    }
  },

  clearError: () => set({ error: null }),
}))
